import React, { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { ClickAwayListener, Tooltip, TooltipProps, styled } from "@mui/material";

const ContentDiv = styled('div')({
    display: 'flex',
    alignItems: 'center'
})

const TooltipContentDiv = styled('div')(({ theme }) => ({
    fontSize: '10px',
    textAlign: 'left',
    fontFamily: 'Montserrat',
    transition: theme.transitions.create('opacity', {
        duration: theme.transitions.duration.shorter,
    }),
    '& a': {
        textDecoration: 'underline',
        color: 'white',
        fontFamily: 'Montserrat-Bold',
    },
    '& a:hover': {
        opacity: 0.8,
        color: 'white',
    }
}))

export type CustomTooltipProps = { disableOpenOnTouch?: boolean, children: React.ReactElement, title: string, interactive?: boolean, hideOnMouseLeave?: boolean, closeAfterTimeout?: number, closeCallback?: () => void, hidden?: boolean } & TooltipProps

const StyledTooltip = styled(Tooltip, {
    shouldForwardProp: (prop) => prop !== 'hidden'
})<{ hidden?: boolean }>(({ hidden }) => ({
    '& .MuiTooltip-tooltip': {
        marginBottom: '-10px',
        fontFamily: 'Montserrat',
        visibility: hidden ? 'hidden' : 'visible',
        '& p': {
            fontSize: '10px',
            lineHeight: '13px',
            textAlign: 'left',
            '&:last-child': {
                margin: 0
            }
        },
        '& strong': {
            fontFamily: 'Montserrat-Bold'
        },
        '& a': {
            textDecoration: 'underline',
            color: 'white',
            fontFamily: 'Montserrat-Bold'
        },
        '& ul': {
            fontSize: '10px',
            marginTop: -6,
            paddingLeft: 0,
            listStyleType: 'none',
            lineHeight: 1.4
        }
    },
    '& .MuiTooltip-tooltipPlacementBottom': {
        margin: "4px 0",
    },
    '& .MuiTooltip-arrow': {
        color: 'inherit'
    }
}));


export const TooltipMaterial = ({ disableOpenOnTouch, children, title, interactive, hideOnMouseLeave = true, closeAfterTimeout, closeCallback, hidden, ...rest }: CustomTooltipProps) => {
    const [open, setOpen] = React.useState(false);
    const isMounted = useRef(false);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false }
    }, []);


    useEffect(() => {
        if(!open) {
            setTimeout(() => {
                if (!isMounted.current) return
                closeCallback?.();
            }, 200);
        }
    }, [open, closeCallback]);

    const handleScroll = () => {
        if (open) {
            setOpen(false);
        }
    }

    let preventOpen = false;

    useEffect(() => {
        document.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('scroll', handleScroll, true);
        }
    });

    const detectTouchStart = (e: React.TouchEvent) => {
        if (disableOpenOnTouch) {
            e.preventDefault()
            preventOpen = true
        }
    };

    const handleTooltipClose = () => {
        if (!isMounted.current) return
        setOpen(false);
    };

    const handleTooltipOpen = (e: any) => {
        if (!isMounted.current || hidden) return
        if (!preventOpen) {
            setOpen(true);
        }
        if (closeAfterTimeout) {
            setTimeout(() => {
                handleTooltipClose();
            }, closeAfterTimeout);
        }
    };

    return (
        <ClickAwayListener onClickAway={handleTooltipClose}>
            <div onMouseLeave={hideOnMouseLeave ? handleTooltipClose : () => { }}>
                <StyledTooltip
                    PopperProps={{
                        disablePortal: true,
                    }}
                    onClose={handleTooltipClose}
                    open={open}
                    disableInteractive={!interactive}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    hidden={!title}
                    title={
                        <React.Fragment>
                            <TooltipContentDiv>
                                <ReactMarkdown children={title} />
                            </TooltipContentDiv>
                        </React.Fragment>
                    }
                    arrow
                    {...rest}
                >
                    <ContentDiv onTouchStart={detectTouchStart} onClick={disableOpenOnTouch ? () => { } : handleTooltipOpen} onMouseEnter={handleTooltipOpen}>
                        {children}
                    </ContentDiv>
                </StyledTooltip>
            </div>
        </ClickAwayListener>
    );
}